import { createFileRoute } from "@tanstack/react-router";

import { type FragmentType, useFragment } from "@repo/graphql-types";
import { graphql } from "@repo/graphql-types/gql";

import { CreateAppointmentBlockedEmptyState } from "@/components/appointment/create-appointment-blocked-empty-state";
import { AppointmentFormFragment } from "@/components/appointment/form/appointment-form";
import { RecurrenceCreateHeader } from "@/components/recurring-appointment/recurrence-create-header";
import { RecurrenceLimitReached } from "@/components/recurring-appointment/recurrence-limit-reached";
import { RecurrencePatternForm } from "@/components/recurring-appointment/recurrence-pattern-form";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { useGraphQL } from "@/hooks/use-graphql";
import { formatWithZonedDate, getCurrentDate } from "@/lib/helpers/date";
import {
  getAppointmentTypesForUnit,
  getUnitsFromSchedules,
} from "@/lib/helpers/recurrence-form-options";
import { isConsultorioVirtual } from "@/lib/helpers/unit";

import type { JSX } from "react";

const RecurringAppointmentCreatePageQuery = graphql(/* GraphQL */ `
  query RecurringAppointmentCreatePageQuery($dataInicio: date!) {
    ...AppointmentFormFragment
    utilizacaoRecorrencia {
      disponivel
      maximoAgendamentosPorBloco
      minimoAgendamentosPorBloco
    }
  }
`);

interface RecurrenceCreateContentProps {
  queryData: FragmentType<typeof AppointmentFormFragment>;
  maxPerBlock: number;
  available: number;
  limitReached: boolean;
}

const RecurrenceCreateContent = ({
  queryData,
  maxPerBlock,
  available,
  limitReached,
}: RecurrenceCreateContentProps): JSX.Element => {
  const { usuariosAgendas } = useFragment(AppointmentFormFragment, queryData);
  const hasCvUnit = getUnitsFromSchedules(usuariosAgendas).some((unit) =>
    isConsultorioVirtual(unit.codUnidade),
  );
  const hasAppointmentTypes = getAppointmentTypesForUnit(usuariosAgendas).length > 0;

  if (!hasAppointmentTypes) {
    return (
      <>
        <FlowHeader>
          <FlowHeader.Actions>
            <FlowHeader.BackButton />
            <FlowHeader.HelpButton />
          </FlowHeader.Actions>
        </FlowHeader>
        <Page>
          <CreateAppointmentBlockedEmptyState />
        </Page>
      </>
    );
  }

  if (limitReached && !hasCvUnit) return <RecurrenceLimitReached />;

  return (
    <>
      <RecurrenceCreateHeader
        title="Agendamento recorrente"
        subtitle="Garanta um horário fixo para um paciente"
      />
      <Page>
        <RecurrencePatternForm
          queryData={queryData}
          maxPerBlock={maxPerBlock}
          available={available}
          limitReached={limitReached}
        />
      </Page>
    </>
  );
};

const RecurringAppointmentCreatePage = (): JSX.Element => {
  const queryResult = useGraphQL(RecurringAppointmentCreatePageQuery, {
    dataInicio: formatWithZonedDate(getCurrentDate(), "yyyy-MM-dd"),
  });

  const { data } = queryResult;

  const utilization = data?.utilizacaoRecorrencia;
  const limitReached = utilization
    ? utilization.disponivel < utilization.minimoAgendamentosPorBloco
    : false;

  return (
    <RoundedLayout>
      <AsyncDataWrapper {...queryResult}>
        {utilization ? (
          <RecurrenceCreateContent
            queryData={data as FragmentType<typeof AppointmentFormFragment>}
            maxPerBlock={utilization.maximoAgendamentosPorBloco}
            available={utilization.disponivel}
            limitReached={limitReached}
          />
        ) : null}
      </AsyncDataWrapper>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/recurring-appointments/create/")({
  component: RecurringAppointmentCreatePage,
});
